import { FC, FormEvent, MouseEvent } from "react";
import { useQueryClient } from '@tanstack/react-query';
import { FormProvider, useForm } from "react-hook-form";

import { Modal } from "@/components/Modal";
import SidePanel, {
  SidePanelBody,
  SidePanelContent,
  SidePanelFooter,
} from "@/components/SidePanel";
import Button from "@/components/Button";
import { FormLayout, Input, Label, Larger, Textarea } from "@/components/Forms";
import { api } from "@/utils/api";
import { handleFormErrors, ucFirst } from "@/utils/helpers";
import ValidationMessage from "@/components/Forms/ValidationMessage";
import useAlerts from "@/hooks/useAlerts";
import { LibraryLinkResource } from "@/types/resources";
import FileField from "@/components/Forms/FileField";

type FormData = {
  name: string;
  link: string;
  image: File | null;
  notes: string;
};

interface Props {
  onClose: () => void;
  linkType: "tool" | "report";
  libraryLink?: LibraryLinkResource;
}

const LinkFormModal: FC<Props> = ({ onClose, linkType, libraryLink }) => {
  const queryClient = useQueryClient();

  const alerts = useAlerts();

  const methods = useForm<FormData>({
    defaultValues: {
      name: libraryLink?.name || "",
      link: libraryLink?.link || "",
      notes: libraryLink?.notes || "",
      image: null,
    },
  });

  const {
    register,
    handleSubmit,
    setError,
    clearErrors,
    formState: { errors, isSubmitting },
  } = methods;

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    clearErrors();
    handleSubmit(async ({ image, ...fields }) => {
      try {
        const formData = new FormData();

        formData.append("type", linkType);

        (Object.keys(fields) as Array<keyof typeof fields>).forEach((key) =>
          formData.append(key, fields[key])
        );

        if (image) {
          formData.append("image", image);
        }

        if (libraryLink) {
          // Update existing resource
          formData.append("_method", "PUT");

          const updatedLink = await api.post<
            typeof formData,
            LibraryLinkResource
          >(`/v1/library-links/${libraryLink.id}`, formData, {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          });

          queryClient.setQueryData(['library-links'], (links: LibraryLinkResource[] | undefined) =>
            links!.map((link) => {
              return link.id === updatedLink.id ? updatedLink : link;
            }));
        } else {
          // Create new resource
          const newLink = await api.post<typeof formData, LibraryLinkResource>(
            `/v1/library-links`,
            formData,
            {
              headers: {
                "Content-Type": "multipart/form-data",
              },
            }
          );

          queryClient.setQueryData(['library-links'], (links: LibraryLinkResource[] | undefined) => [
            ...(links || []),
            newLink,
          ]);
        }

        onClose();

        alerts.success(
          `${ucFirst(linkType)} was successfully ${
            !!libraryLink ? "updated" : "added"
          }`
        );
      } catch (error) {
        handleFormErrors<FormData>(error, setError);
      }
    })(e);
  };

  const onCancel = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    onClose();
  };

  return (
    <Modal>
      <SidePanel
        heading={`${libraryLink ? "Edit" : "Add"} ${linkType}`}
        onClose={onClose}
      >
        <FormProvider {...methods}>
          <SidePanelBody onSubmit={onSubmit} autoComplete="off">
            <SidePanelContent>
              <FormLayout>
                <Larger>
                  <FileField fileType="image" label="Image" />
                </Larger>
                <Larger>
                  <Label htmlFor="nameInput">Name</Label>
                  <Input
                    $hasError={!!errors.name}
                    id="nameInput"
                    type="text"
                    placeholder="Add name"
                    {...register("name")}
                  />
                  {!!errors.name && (
                    <ValidationMessage>{errors.name.message}</ValidationMessage>
                  )}
                </Larger>
                <Larger>
                  <Label htmlFor="linkInput">Content Link</Label>
                  <Input
                    $hasError={!!errors.link}
                    id="linkInput"
                    type="url"
                    placeholder="Add link"
                    {...register("link")}
                  />
                  {!!errors.link && (
                    <ValidationMessage>{errors.link.message}</ValidationMessage>
                  )}
                </Larger>
                <Larger>
                  <Label htmlFor="notesTextarea">Notes</Label>
                  <Textarea
                    id="notesTextarea"
                    placeholder="Add notes"
                    $hasError={!!errors.notes}
                    {...register("notes")}
                  />
                  {!!errors.notes && (
                    <ValidationMessage>
                      {errors.notes.message}
                    </ValidationMessage>
                  )}
                </Larger>
              </FormLayout>
            </SidePanelContent>
            <SidePanelFooter $actions>
              <Button disabled={isSubmitting} loading={isSubmitting}>
                {libraryLink ? "Update" : "Add"}
              </Button>
              <Button color="secondary" onClick={onCancel}>
                Cancel
              </Button>
            </SidePanelFooter>
          </SidePanelBody>
        </FormProvider>
      </SidePanel>
    </Modal>
  );
};

export default LinkFormModal;
